import React from "react";
import { Link } from "react-router-dom";
import "../styles/Home.css";
import LoopVideo from "../components/LoopVideo";
import ContactBand from "../components/ContactBand";
import heroVideo from "../assets/media/hero-lathe.mp4";
import heroPoster from "../assets/media/hero-lathe-poster.jpg";
import { equipment, capabilities, materials, industries, testimonials } from "../data/site";

function Plate({ item }) {
	return (
		<figure className={`plate plate--span-${item.span}`}>
			<div className="plate__media" style={{ aspectRatio: item.ratio }}>
				{item.kind === "video" ? (
					<LoopVideo src={item.src} poster={item.poster} label={item.alt} />
				) : (
					<img
						src={item.srcSmall}
						srcSet={`${item.srcSmall} 800w, ${item.src} 1600w`}
						sizes={`(max-width: 720px) 100vw, ${Math.round((item.span / 12) * 100)}vw`}
						alt={item.alt}
						loading="lazy"
						decoding="async"
					/>
				)}
			</div>
			<figcaption>
				<span className="plate__name">{item.name}</span>
				<span className="plate__note">{item.note}</span>
			</figcaption>
		</figure>
	);
}

export default function Home() {
	return (
		<>
			<title>B & H Machining | Precision ceramic, quartz, glass and metal machining</title>
			<meta
				name="description"
				content="Diamond grinding, core drilling and CNC machining of ceramic, quartz, glass and metal parts in San Carlos, CA. Prototype to full production."
			/>

			<section className="hero" aria-labelledby="hero-title">
				<LoopVideo
					className="hero__video"
					src={heroVideo}
					poster={heroPoster}
					label="CNC lathe turning a part under coolant spray"
					eager
				/>
				<div className="hero__content wrap">
					<h1 id="hero-title">From engineering concept to high production</h1>
					<p className="hero__sub">
						Diamond grinding, core drilling and CNC machining of ceramic, quartz, glass and
						metal components. Made in San Carlos, California, for more than 40 years.
					</p>
					<div className="hero__actions">
						<Link to="/quote" className="button button--primary">
							Request a quote
						</Link>
						<a href="#equipment" className="button button--ghost">
							See the equipment
						</a>
					</div>
				</div>
			</section>

			<section className="section intro" aria-label="About B & H Machining">
				<div className="wrap intro__grid">
					<p className="intro__statement">
						B & H Machining makes the precision parts that go into electronic and mechanical
						products, from the first prototype to full line production.
					</p>
					<div className="intro__body">
						<p>
							We specialize in ceramic and quartz, and we also work in glass and metal to your
							exact specifications, small and large. Our reputation rests on repeatability:
							the hundredth part matches the first.
						</p>
						<p>
							With a deep supply of ceramics, quartz and glass on hand, we can fill volume
							orders with shorter lead times and better pricing.
						</p>
					</div>
				</div>
			</section>

			<section id="equipment" className="section section--alumina" aria-labelledby="equipment-title">
				<div className="wrap">
					<div className="section-head">
						<h2 id="equipment-title">New equipment on the floor</h2>
						<p>
							We've added CNC turning and milling centers to the shop. More capacity means
							tighter schedules for your production runs.
						</p>
					</div>
					<div className="plates">
						{equipment.map((item) => (
							<Plate key={item.name} item={item} />
						))}
					</div>
				</div>
			</section>

			<section className="section" aria-labelledby="capabilities-title">
				<div className="wrap">
					<div className="section-head">
						<h2 id="capabilities-title">What we do</h2>
						<p>
							Fabrication, grinding, drilling, milling, turning and finishing, all under one
							roof.
						</p>
					</div>
					<dl className="spec-list">
						{capabilities.map((c) => (
							<div key={c.name}>
								<dt>{c.name}</dt>
								<dd>{c.text}</dd>
							</div>
						))}
					</dl>
					<p className="more-link">
						<Link to="/services">All capabilities and materials</Link>
					</p>
				</div>
			</section>

			<section className="section section--alumina" aria-labelledby="materials-title">
				<div className="wrap split">
					<div>
						<h2 id="materials-title">Materials</h2>
					</div>
					<dl className="stack-list">
						{materials.map((m) => (
							<div key={m.name}>
								<dt>{m.name}</dt>
								<dd>{m.text}</dd>
							</div>
						))}
					</dl>
				</div>
			</section>

			<section className="section" aria-labelledby="industries-title">
				<div className="wrap split">
					<div>
						<h2 id="industries-title">Industries we serve</h2>
					</div>
					<dl className="stack-list">
						{industries.map((i) => (
							<div key={i.name}>
								<dt>{i.name}</dt>
								<dd>{i.text}</dd>
							</div>
						))}
					</dl>
				</div>
			</section>

			<section className="section section--alumina" aria-labelledby="trust-title">
				<div className="wrap">
					<h2 id="trust-title" className="trust__title">
						A qualified and certified supplier
					</h2>
					<div className="trust">
						{testimonials.map((t) => (
							<figure key={t.source}>
								<blockquote>“{t.quote}”</blockquote>
								<figcaption>{t.source}</figcaption>
							</figure>
						))}
					</div>
				</div>
			</section>

			<ContactBand />
		</>
	);
}
